import SimpleArtist from '@common/types/artist/simple-artist';
import Avatar from '@components/basics/avatar';
import { useOutsideClick } from '@webapp/hooks/useOutsideClick';
import AppState from '@webapp/store/state';
import React, { FC, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import styled from 'styled-components';
import DarkLightCtrl from '@webapp/controllers/dark-light-dropdown';
import CustomUrlInputCtrl from '@webapp/controllers/artist/artist-profile/custom-url-input';
import { Dispatch } from 'redux';
import CustomDomain from '@components/artist/custom-domain';
import TracPage from '@services/tracpage/types/trac-page';
import { motion } from 'framer-motion';
import SocialNetworkListCtrl from '@webapp/controllers/form/social-network-list';
import {
  UpdateTracPageDataField,
  UpdateTracPageField,
} from '@webapp/store/tracpage/action-creators';
import { TracThemeType } from '@services/tracpage/types/trac-theme-type';
import { TracPageSocialLink } from '@services/tracpage/types/track-page-social-link';

const UPDATE_ACTION_CONFIG = {
  noAutosave: true,
  updateDraft: true,
};

enum SettingsTab {
  GENERAL,
  SOCIAL,
}

const TABS = [
  {
    tab: SettingsTab.GENERAL,
    label: <Translate id="PROFILE.SETTINGS_DIALOG.TAB.GENERAL" />,
  },
  {
    tab: SettingsTab.SOCIAL,
    label: <Translate id="PROFILE.SETTINGS_DIALOG.TAB.SOCIAL" />,
  },
];

type StateProps = {
  artist: SimpleArtist | null;
  pageData: TracPage;
};
type DispatchProps = {
  updateTheme: (theme: TracThemeType) => UpdateTracPageField<TracPage, 'theme'>;
  updateSocials: (
    socials: TracPageSocialLink[]
  ) => UpdateTracPageDataField<TracPage, 'social'>;
};
type Props = {
  onClose: () => void;
} & StateProps &
  DispatchProps;

const mapStateToProps = (state: AppState): StateProps => ({
  artist:
    state.artist.myArtists.find((a) => a.id === state.activeProfile.id) ?? null,
  pageData: state.tracpage.artistPageDraft,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  updateTheme: (theme) =>
    dispatch(
      new UpdateTracPageField('tracpage', 'theme', theme, UPDATE_ACTION_CONFIG)
    ),
  updateSocials: (socials) =>
    dispatch(
      new UpdateTracPageDataField(
        'tracpage',
        'social',
        socials,
        UPDATE_ACTION_CONFIG
      )
    ),
});

const SettingsDialog: FC<Props> = ({
  onClose,
  artist,
  pageData,
  updateTheme,
  updateSocials,
}) => {
  const [tab, setTab] = useState(SettingsTab.GENERAL);
  const dialogRef = useOutsideClick<HTMLDivElement>(onClose);

  const [theme, setTheme] = useState(pageData.theme);
  const [networks, setNetworks] = useState(pageData.data.social);

  const handleSave = () => {
    updateTheme(theme);
    updateSocials(networks);
    onClose();
  };

  return (
    <DialogWrapper
      transition={{ duration: 0.2 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      ref={dialogRef}
    >
      {artist && <DialogHeader artist={artist} />}
      <Tabs tab={tab} onSelect={(t) => setTab(t)} />
      <div style={{ flex: 1 }}>
        {tab === SettingsTab.GENERAL && (
          <>
            <DarkLightCtrl
              isGrey
              value={theme}
              className="mb-10"
              onChange={setTheme}
            />
            <CustomUrlInputCtrl isGrey enableEditing={false} />
            <CustomDomain isLocked={true} />
          </>
        )}
        {tab === SettingsTab.SOCIAL && (
          <SocialNetworkListCtrl
            isGrey
            withoutHeader
            onChange={setNetworks}
            networks={networks}
          />
        )}
      </div>
      <ActionsContainer>
        <button onClick={handleSave} className="save-button">
          <Translate id="PROFILE.SETTINGS_DIALOG.SAVE" />
        </button>
      </ActionsContainer>
    </DialogWrapper>
  );
};

const Tabs = ({
  tab,
  onSelect,
}: {
  tab: SettingsTab;
  onSelect: (tab: SettingsTab) => void;
}) => {
  return (
    <TabsContainer>
      {TABS.map((t) => (
        <button
          key={t.tab}
          className={`tab ${t.tab === tab ? 'active' : ''}`}
          onClick={() => onSelect(t.tab)}
        >
          {t.label}
        </button>
      ))}
    </TabsContainer>
  );
};

const DialogHeader = ({ artist }: { artist: SimpleArtist }) => {
  artist.type;
  return (
    <DialogHeaderContainer>
      <Avatar src={artist.avatar} />
      <div className="artist-info">
        <span className="name">{artist.legalName ?? artist.name}</span>
        <span className="subtitle">Personal Profile</span>
      </div>
    </DialogHeaderContainer>
  );
};

const ActionsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: end;
  align-items: center;

  & > .save-button {
    background: transparent;
    color: ${({ theme }) => theme.colors.black};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  margin-bottom: 24px;

  & > .tab {
    font-size: 14px;
    font-weight: 600;
    color: #98999d;
    background-color: transparent;

    cursor: pointer;

    &.active {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

const DialogHeaderContainer = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 10px;

  & > .artist-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;

    & > .name {
      font-size: 14px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.black};
    }

    & > .subtitle {
      font-size: 12px;
      font-weight: 400;
      color: #a1a5b4;
    }
  }
`;

const DialogWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;

  position: fixed;
  z-index: 9999;
  bottom: 0;
  left: 0;

  width: 100%;
  min-height: 60%;

  padding: 30px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 10px 10px 0 0;

  @media ${({ theme }) => theme.device.tablet} {
    bottom: 174px;
    right: 26px;
    left: inherit;
    width: 368px;
    min-height: 425px;
    border-radius: 10px;
  }
`;

export default connect(mapStateToProps, mapDispatchToProps)(SettingsDialog);
